<template>
  <div class="printer-page">
    <breadcrumb :breadcrumbList="breadcrumbPrinter"></breadcrumb>
    <div class="main-content">
      <div class="toolbar">
        <el-button type="primary" class="add-btn" @click="openCreateDialog" v-has="'printer:add'">
          添加打印机
        </el-button>
        <div class="status-tip">
          <span class="tip-dot"></span>
          <span>打印机状态每 10 分钟自动检测一次</span>
        </div>
      </div>

      <el-table :data="dataList" class="printer-table" v-loading="loading">
        <el-table-column prop="printerName" label="备注名称" min-width="120">
          <template #default="{ row }">
            <span>{{ row.printerName || row.printSn || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="打印机型号" min-width="260">
          <template #default>
            <span>{{ printerModelName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="打印宽度" min-width="100">
          <template #default="{ row }">
            <span>{{ row.printWidth || 58 }}mm</span>
          </template>
        </el-table-column>
        <el-table-column prop="printSn" label="打印机机身号" min-width="180" />
        <el-table-column label="所在门店" min-width="180">
          <template #default="{ row }">
            <span>{{ row.shopName || '-' }}{{ row.isDisable ? '（已禁用）' : '' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="打印机状态" min-width="150">
          <template #default="{ row }">
            <div class="printer-status">
              <div>
                <span class="status-dot" :class="statusClass(row.status)"></span>
                <span :class="statusClass(row.status)">{{ statusText(row) }}</span>
              </div>
              <p v-if="row.lastSyncTime">最后检测 {{ formatTime(row.lastSyncTime) }}</p>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120">
          <template #default="{ row }">
            <el-button type="text" @click="openEditDialog(row)" v-has="'printer:edit'">编辑</el-button>
            <el-button type="text" class="danger-btn" @click="handleDelete(row)" v-has="'printer:delete'">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        v-model:currentPage="queryInfo.current"
        v-model:page-size="queryInfo.size"
        :page-sizes="[10, 20, 30, 40]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="dataTotal"
        @size-change="initPage"
        @current-change="initPage"
      />
    </div>

    <el-dialog
      v-model="dialogVisible"
      width="560px"
      :title="dialogTitle"
      custom-class="printer-dialog"
      align-center
      @close="resetForm"
    >
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-position="left"
        label-width="140px"
        class="printer-form"
      >
        <el-form-item label="打印机型号">
          <div class="model-chip">{{ printerModelName }}</div>
        </el-form-item>
        <el-form-item label="打印宽度" prop="printWidth">
          <el-radio-group v-model="form.printWidth">
            <el-radio :label="58">58mm</el-radio>
            <el-radio :label="80">80mm</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注名称" prop="printerName">
          <el-input v-model="form.printerName" placeholder="请输入备注名称" />
        </el-form-item>
        <el-form-item label="打印机机身号" prop="printSn">
          <el-input v-model="form.printSn" placeholder="请输入打印机机身号" />
        </el-form-item>
        <el-form-item label="所在门店" prop="shopId">
          <el-select v-model="form.shopId" placeholder="请选择门店" filterable>
            <el-option
              v-for="item in shopList"
              :key="item.shopId"
              :label="item.shopName"
              :value="item.shopId"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saveLoading" @click="handleSave">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="js">
import { defineComponent, reactive, ref } from 'vue'
import Breadcrumb from '@/components/breadcrumb/breadcrumb.vue'
import { breadcrumbPrinter } from '../config'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getShopDownList } from '@/service/main/shop'
import { createPrinter, deletePrinter, editPrinter, getPrinterPage } from '@/service/main/printer'

const printerModelName = '芯烨58IINT【USB+WIFI】蜂鸣款'

export default defineComponent({
  components: {
    Breadcrumb
  },
  setup() {
    const loading = ref(false)
    const saveLoading = ref(false)
    const dialogVisible = ref(false)
    const dialogTitle = ref('新增打印机')
    const formRef = ref()
    const dataList = ref([])
    const shopList = ref([])
    const dataTotal = ref(0)
    const queryInfo = reactive({
      current: 1,
      size: 10
    })
    const form = reactive({
      id: null,
      supplierType: 1,
      printerName: '',
      printSn: '',
      printWidth: 58,
      shopId: null,
      isDisable: false
    })
    const rules = {
      printerName: [{ required: true, message: '请输入备注名称', trigger: 'blur' }],
      printSn: [{ required: true, message: '请输入打印机机身号', trigger: 'blur' }],
      printWidth: [{ required: true, message: '请选择打印宽度', trigger: 'change' }],
      shopId: [{ required: true, message: '请选择门店', trigger: 'change' }]
    }

    const initPage = async () => {
      loading.value = true
      try {
        const res = await getPrinterPage({ ...queryInfo })
        if (res.code === 0) {
          dataList.value = res.data.list || []
          dataTotal.value = Number(res.data.total || 0)
        } else {
          ElMessage.error(res.msg || '获取打印机列表失败')
        }
      } finally {
        loading.value = false
      }
    }

    const initShopList = async () => {
      const res = await getShopDownList()
      if (res.code === 0) {
        shopList.value = (res.data || []).map((item) => ({
          shopId: Number(item.shopId || item.id),
          shopName: item.shopName
        }))
      }
    }

    const resetForm = () => {
      form.id = null
      form.supplierType = 1
      form.printerName = ''
      form.printSn = ''
      form.printWidth = 58
      form.shopId = null
      form.isDisable = false
      if (formRef.value) {
        formRef.value.clearValidate()
      }
    }

    const openCreateDialog = () => {
      dialogTitle.value = '新增打印机'
      resetForm()
      dialogVisible.value = true
    }

    const openEditDialog = (row) => {
      dialogTitle.value = '编辑打印机'
      resetForm()
      form.id = row.id
      form.supplierType = row.supplierType || 1
      form.printerName = row.printerName || ''
      form.printSn = row.printSn || ''
      form.printWidth = row.printWidth || 58
      form.shopId = Number(row.shopId)
      form.isDisable = !!row.isDisable
      dialogVisible.value = true
    }

    const buildSaveParam = () => {
      const param = {
        supplierType: 1,
        printerName: form.printerName && form.printerName.trim(),
        printSn: form.printSn && form.printSn.trim(),
        printWidth: form.printWidth,
        shopId: form.shopId,
        isDisable: form.isDisable
      }
      if (form.id) {
        param.id = form.id
      }
      return param
    }

    const handleSave = () => {
      formRef.value.validate(async (valid) => {
        if (!valid) {
          return
        }
        saveLoading.value = true
        try {
          const request = form.id ? editPrinter : createPrinter
          const res = await request(buildSaveParam())
          if (res.code === 0) {
            ElMessage.success('保存成功')
            dialogVisible.value = false
            initPage()
          } else {
            ElMessage.error(res.msg || '保存失败')
          }
        } finally {
          saveLoading.value = false
        }
      })
    }

    const handleDelete = (row) => {
      ElMessageBox.confirm('确认删除该打印机吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const res = await deletePrinter(row.id)
        if (res.code === 0) {
          ElMessage.success('删除成功')
          initPage()
        } else {
          ElMessage.error(res.msg || '删除失败')
        }
      }).catch(() => {})
    }

    const statusClass = (status) => {
      if (status === 1) {
        return 'online'
      }
      if (status === 0) {
        return 'offline'
      }
      if (status === 2) {
        return 'abnormal'
      }
      return 'unknown'
    }

    const statusText = (row) => {
      if (row.statusDesc) {
        return row.statusDesc
      }
      if (row.status === 1) {
        return '在线'
      }
      if (row.status === 0) {
        return '离线'
      }
      if (row.status === 2) {
        return '异常'
      }
      return '未检测'
    }

    const formatTime = (time) => {
      if (!time) {
        return ''
      }
      const text = String(time).replace('T', ' ')
      return text.length >= 16 ? text.substring(11, 16) : text
    }

    initShopList()
    initPage()

    return {
      breadcrumbPrinter,
      dataList,
      dataTotal,
      dialogTitle,
      dialogVisible,
      form,
      formRef,
      formatTime,
      handleDelete,
      handleSave,
      initPage,
      loading,
      openCreateDialog,
      openEditDialog,
      printerModelName,
      queryInfo,
      resetForm,
      rules,
      saveLoading,
      shopList,
      statusClass,
      statusText
    }
  }
})
</script>

<style scoped lang="less">
.printer-page {
  display: flex;
  flex-direction: column;
  min-width: 1080px;
  min-height: calc(100vh - 90px);

  .main-content {
    flex: 1;
    min-height: 0;
    margin: 20px;
    padding: 20px;
    background-color: #fff;
    border-radius: 6px;
  }

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  .add-btn {
    width: 120px;
    height: 36px;
    font-size: 14px;
    border-radius: 4px;
  }

  .status-tip {
    display: flex;
    align-items: center;
    color: #778195;
    font-size: 12px;
  }

  .tip-dot {
    width: 6px;
    height: 6px;
    margin-right: 6px;
    border-radius: 50%;
    background-color: #08a63f;
  }

  .printer-table {
    font-size: 12px;

    &:deep(.el-table__header th) {
      height: 40px;
      background-color: #f6f7fa;
      color: #40506a;
      font-size: 14px;
      font-weight: 500;
    }

    &:deep(.el-table__body td) {
      height: 50px;
      color: #000;
    }
  }

  .printer-status {
    line-height: 1.4;

    .status-dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      margin-right: 6px;
      border-radius: 50%;
      vertical-align: middle;
    }

    .status-dot.online {
      background-color: #00a944;
    }

    span.online:not(.status-dot) {
      color: #00a944;
      font-weight: 500;
    }

    .status-dot.offline {
      background-color: #9aa3b2;
    }

    span.offline:not(.status-dot) {
      color: #9aa3b2;
      font-weight: 500;
    }

    .status-dot.abnormal {
      background-color: #ff8a00;
    }

    span.abnormal:not(.status-dot) {
      color: #ff8a00;
      font-weight: 500;
    }

    .status-dot.unknown {
      background-color: #9aa3b2;
    }

    span.unknown:not(.status-dot) {
      color: #9aa3b2;
      font-weight: 500;
    }

    p {
      margin: 2px 0 0 14px;
      color: #778195;
      font-size: 12px;
    }
  }

  .danger-btn {
    color: #ff3333;
  }

  .pagination {
    margin-top: 15px;
    justify-content: flex-end;
  }

  .printer-form {
    padding: 0;

    &:deep(.el-form-item) {
      margin-bottom: 18px;
    }

    &:deep(.el-form-item__label) {
      color: #606266;
      font-size: 14px;
    }

    &:deep(.el-form-item__label::before) {
      color: #f56c6c;
    }

    &:deep(.el-input__inner) {
      height: 32px;
      font-size: 14px;
    }

    &:deep(.el-select) {
      width: 100%;
    }

    &:deep(.el-radio__label) {
      font-size: 14px;
      color: #606266;
    }
  }

  .model-chip {
    display: inline-flex;
    align-items: center;
    min-height: 32px;
    padding: 0 12px;
    border-radius: 4px;
    color: rgb(121, 72, 234);
    font-size: 14px;
    font-weight: 500;
    border: 1px solid rgb(121, 72, 234);
  }
}
</style>

<style lang="less">
.printer-dialog {
  border-radius: 6px;

  .el-dialog__header {
    padding: 16px 20px;
    margin-right: 0;
    border-bottom: 1px solid #e4e7ed;
  }

  .el-dialog__title {
    color: #303133;
    font-size: 16px;
    font-weight: 500;
  }

  .el-dialog__body {
    padding: 20px 20px 0;
  }

  .el-dialog__footer {
    padding: 10px 20px 20px;

    .el-button {
      width: 68px;
      height: 32px;
      font-size: 14px;
    }
  }
}
</style>
